/**
 * label_raster extension handler.
 *
 * One annotation per (module, overlay series). The edit is persisted as a
 * CBOR chunk-diff manifest under field_key='chunk_diffs_cbor'; the API layer
 * materializes any *_cbor blob into annotation_files
 * (data/projects/<pid>/cases/<cid>/annotations/<id>/chunk_diffs_cbor.cbor).
 * Scalar summary rows (domain, base_series_id, edited_chunks, changed_pixels,
 * classes_used) stay in annotation_data for export without decoding the blob.
 */
import { BaseExtensionHandler } from "../_base.ts"
import type {
  ClientValidationContext,
  ExtensionManifest,
  SerializedAnnotation,
  StoredAnnotation
} from "../_interface.ts"
import {
  decodeLabelRasterManifest,
  encodeLabelRasterManifest,
  type LabelRasterManifestJson,
  manifestFromJson,
  manifestToJson
} from "./manifest.ts"
import {
  createLabelRasterSchema,
  labelRasterConfigSchema,
  labelRasterProjectConfigSchema,
  updateLabelRasterSchema
} from "./schema.ts"

export class LabelRasterHandler extends BaseExtensionHandler {
  readonly type = "label_raster"
  readonly manifest: ExtensionManifest = {
    type: "label_raster",
    label: "Label Raster Edit",
    scope: "series",
    storage: "both",
    supportsLinking: false
  }

  getConfigSchema() {
    return labelRasterConfigSchema
  }

  getCreateSchema() {
    return createLabelRasterSchema
  }

  getUpdateSchema() {
    return updateLabelRasterSchema
  }

  override getProjectConfigSchema() {
    return labelRasterProjectConfigSchema
  }

  /** Edit regions follow the overlay domain the module edits. */
  override getEditRegionDomain(templateSchema: unknown, _projectConfig: unknown): string | null {
    const domain = (templateSchema as { domain?: unknown } | null)?.domain
    return typeof domain === "string" && domain.length > 0 ? domain : null
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as {
      domain?: string
      base_series_id?: string | null
      manifest?: LabelRasterManifestJson
    }

    const rows: SerializedAnnotation["data"] = []
    if (data.domain !== undefined) {
      rows.push({ field_key: "domain", value_text: data.domain })
    }
    if (data.base_series_id !== undefined) {
      rows.push({ field_key: "base_series_id", value_text: data.base_series_id ?? null })
    }
    if (data.manifest !== undefined) {
      const manifest = manifestFromJson(data.manifest)
      rows.push({ field_key: "edited_chunks", value_int: manifest.chunks.length })
      rows.push({
        field_key: "changed_pixels",
        value_int: manifest.chunks.reduce((n, c) => n + c.changedPixels, 0)
      })
      rows.push({ field_key: "classes_used", value_text: manifest.classesUsed.join(",") })
      rows.push({ field_key: "chunk_diffs_cbor", value_blob: encodeLabelRasterManifest(manifest) })
    }

    return {
      annotation: {
        label_name: data.domain ?? null,
        label_value: null
      },
      data: rows,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const text = (key: string) => stored.data.find((r) => r.field_key === key)?.value_text ?? null
    const int = (key: string) => stored.data.find((r) => r.field_key === key)?.value_int ?? null
    const blobRow = stored.data.find((r) => r.field_key === "chunk_diffs_cbor")
    const decoded = blobRow?.value_blob ? decodeLabelRasterManifest(blobRow.value_blob) : null
    const file = stored.files.find((f) => f.file_type === "chunk_diffs_cbor")

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      domain: text("domain") ?? stored.annotation.label_name,
      base_series_id: text("base_series_id"),
      edited_chunks: int("edited_chunks"),
      changed_pixels: int("changed_pixels"),
      classes_used: (text("classes_used") ?? "")
        .split(",")
        .filter((s) => s.length > 0)
        .map(Number),
      manifest: decoded ? manifestToJson(decoded) : null,
      chunk_diffs_path: file?.storage_path ?? null,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return [
      "label_raster_domain",
      "label_raster_edited_chunks",
      "label_raster_changed_pixels",
      "label_raster_classes_used",
      "label_raster_manifest_path"
    ]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    const domains: string[] = []
    const paths: string[] = []
    const classes = new Set<number>()
    let chunks = 0
    let pixels = 0
    for (const a of annotations) {
      const domain = a.data.find((r) => r.field_key === "domain")?.value_text ?? a.annotation.label_name
      if (domain) domains.push(domain)
      chunks += a.data.find((r) => r.field_key === "edited_chunks")?.value_int ?? 0
      pixels += a.data.find((r) => r.field_key === "changed_pixels")?.value_int ?? 0
      for (const c of (a.data.find((r) => r.field_key === "classes_used")?.value_text ?? "").split(",")) {
        if (c) classes.add(Number(c))
      }
      const file = a.files.find((f) => f.file_type === "chunk_diffs_cbor")
      if (file) paths.push(file.storage_path)
    }
    return {
      label_raster_domain: domains.join("; "),
      label_raster_edited_chunks: chunks,
      label_raster_changed_pixels: pixels,
      label_raster_classes_used: [...classes].sort((a, b) => a - b).join(","),
      label_raster_manifest_path: paths.join("; ")
    }
  }

  override validateCompletionClient(context: ClientValidationContext): { valid: boolean; errors: string[] } {
    // Only per-project overrides are available client-side (module.config);
    // the template default for require_edit is false.
    const overrides = labelRasterProjectConfigSchema.safeParse(
      context.module.config ? safeJsonParse(context.module.config) : {}
    )
    const requireEdit = overrides.success ? overrides.data.require_edit === true : false
    if (!requireEdit) return { valid: true, errors: [] }

    const edits = (context.labelRasters ?? []).filter(
      (r) => r.projectModuleId === context.module.id && r.changedPixels > 0
    )
    if (edits.length === 0) {
      return { valid: false, errors: [`${context.module.template_name}: no label edits recorded`] }
    }
    return { valid: true, errors: [] }
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return {}
  }
}
